import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { endOfDay } from 'date-fns';
import { usePostsStore } from '@/stores/postsStore';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { TagsMultiSelect } from '@/components/ui/TagsMultiSelect';
import { PlatformMultiSelect } from '@/components/ui/PlatformMultiSelect';
import { useLabelOptions } from '@/hooks/useLabelOptions';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useEscapeLayer } from '@/hooks/useEscapeLayer';
import { useDrawerFocus } from '@/hooks/useDrawerFocus';

/**
 * PostFilterDrawer Component
 *
 * Slide-out drawer from the right with filter controls:
 * - Status filter (All, Draft, Scheduled, etc.)
 * - Platform filter (All, LinkedIn, Facebook, etc.)
 * - Date range picker (From/To)
 * - Search input
 * - Apply and Clear filters buttons
 *
 * Features:
 * - Slides in from right
 * - Dark backdrop overlay
 * - Closes on ESC key
 * - Closes on backdrop click
 * - Focus trap when open
 */

interface PostFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PostFilterDrawer = ({ isOpen, onClose }: PostFilterDrawerProps) => {
  const { t } = useTranslation('common');
  const { filters, setFilter, clearFilters } = usePostsStore();
  const { isAgencyTeam, carteBlanche } = useReviewerRestrictions();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Show approval filter only for agency teams without carte blanche
  const showApprovalFilter = isAgencyTeam && !carteBlanche;

  // Move constant arrays INSIDE component so they can access t()
  const CONTENT_STATUS_OPTIONS = [
    { value: 'all', label: t('all_content_statuses'), icon: ICONS.FOLDER },
    { value: 'DRAFT', label: t('draft'), icon: ICONS.EDIT },
    { value: 'READY', label: t('ready_to_shoot'), icon: ICONS.CHECK_CIRCLE },
  ];

  const PUBLISHING_STATUS_OPTIONS = [
    { value: 'all', label: t('all_publishing_statuses'), icon: ICONS.FOLDER },
    { value: 'unmapped', label: t('unmapped'), icon: ICONS.FILE },
    { value: 'mapped', label: t('mapped'), icon: ICONS.CALENDAR },
    { value: 'scheduled', label: t('scheduled'), icon: ICONS.CLOCK },
    { value: 'publishing', label: t('publishing'), icon: ICONS.UPLOAD },
    { value: 'published', label: t('published'), icon: ICONS.CHECK_CIRCLE },
    { value: 'failed', label: t('failed'), icon: ICONS.CROSS_CIRCLE },
  ];

  const SORT_OPTIONS = [
    { value: 'newest', label: t('newest_first'), icon: ICONS.ARROW_DOWN },
    { value: 'oldest', label: t('oldest_first'), icon: ICONS.ARROW_UP },
    { value: 'scheduled_asc', label: t('scheduled_date_asc'), icon: ICONS.CALENDAR },
    { value: 'scheduled_desc', label: t('scheduled_date_desc'), icon: ICONS.CALENDAR },
  ];

  const MEDIA_TYPE_OPTIONS = [
    { value: 'all', label: t('all_media_types') },
    { value: 'image', label: t('with_images') },
    { value: 'video', label: t('with_videos') },
    { value: 'pdf', label: t('with_pdfs') },
    { value: 'none', label: t('no_media') },
  ];

  const WORKFLOW_STATUS_OPTIONS = [
    { value: 'all', label: t('all_approval_statuses'), icon: ICONS.FOLDER },
    { value: 'PENDING', label: t('pending_approval'), icon: ICONS.CLOCK },
    { value: 'CHANGES_REQUESTED', label: t('changes_requested'), icon: ICONS.EDIT },
    { value: 'APPROVED', label: t('approved'), icon: ICONS.CHECK_CIRCLE },
  ];

  // Fetch labels with actual post counts for tags filter (only when drawer is open)
  const { data: labels } = useLabelOptions({ enabled: isOpen });
  const availableLabels = labels || [];

  const hasActiveFilters =
    (filters.contentStatus) ||
    (filters.publishingStatus && filters.publishingStatus !== 'all') ||
    (filters.platforms && filters.platforms.length > 0 && !(filters.platforms.length === 1 && filters.platforms[0] === 'ALL')) ||
    filters.dateFrom !== null ||
    filters.dateTo !== null ||
    filters.search !== '' ||
    filters.tags !== '' ||
    (filters.mediaType && filters.mediaType !== 'all') ||
    (filters.workflowStatus && filters.workflowStatus !== 'all');

  /**
   * Date range for DateRangePicker
   */
  const dateRange = {
    from: filters.dateFrom ? new Date(filters.dateFrom) : null,
    to: filters.dateTo ? new Date(filters.dateTo) : null,
  };

  /**
   * Handle date range change
   */
  const handleDateRangeChange = (range: { from: Date | null; to: Date | null }) => {
    setFilter('dateFrom', range.from?.toISOString() || null);
    setFilter('dateTo', range.to ? endOfDay(range.to).toISOString() : null);
  };

  /**
   * Tags selection handling
   */
  const selectedTags = filters.tags ? filters.tags.split(',').filter(Boolean) : [];

  const handleTagsChange = (tags: string[]) => {
    setFilter('tags', tags.length > 0 ? tags.join(',') : '');
  };

  /**
   * ESC closes this drawer only when it is the topmost layer and no inner popover consumed the key.
   */
  useEscapeLayer(isOpen, onClose);

  /**
   * Prevent body scroll when drawer is open
   */
  useBodyScrollLock(isOpen);

  // Inert + aria-hidden while closed; focus in on open, restore to the opener on close
  useDrawerFocus(isOpen, drawerRef);

  return (
    <div
      className={`fixed inset-0 z-40 transition-opacity duration-300 ease-in-out ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`fixed right-0 top-navbar-height bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:bottom-0 w-full sm:max-w-md bg-white shadow-2xl z-50 overflow-y-auto transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal={isOpen ? 'true' : undefined}
        aria-labelledby="filter-drawer-title"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name={ICONS.FILTER} size="sm" color="text-gray-600" />
            <h2 id="filter-drawer-title" className="text-sm lg:text-lg font-bold text-gray-900">
              {t('filter_posts')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('close_filter_drawer')}
          >
            <Icon name={ICONS.CROSS} size="sm" color="text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
          {/* Search */}
          <div>
            <Input
              inputSize="sm"
              type="search"
              id="search-filter"
              placeholder={t('search_posts')}
              value={filters.search}
              onChange={(e) => setFilter('search', e.target.value)}
              className="w-full"
            />
          </div>

          {/* Approval Status Filter (Agency only) — shown near the top for quick access */}
          {showApprovalFilter && (
            <div>
              <Select
                size="sm"
                label={t('approval_status')}
                value={filters.workflowStatus || 'all'}
                onValueChange={(value) => setFilter('workflowStatus', value === 'all' ? null : value)}
                options={WORKFLOW_STATUS_OPTIONS}
                className="w-full"
              />
            </div>
          )}

          {/* Content Status Filter */}
          <div>
            <Select
              size="sm"
              label={t('content_status')}
              value={filters.contentStatus || 'all'}
              onValueChange={(value) => setFilter('contentStatus', value === 'all' ? null : value)}
              options={CONTENT_STATUS_OPTIONS}
              className="w-full"
            />
          </div>

          {/* Publishing Status Filter */}
          <div>
            <Select
              size="sm"
              label={t('publishing_status')}
              value={filters.publishingStatus || 'all'}
              onValueChange={(value) => setFilter('publishingStatus', value === 'all' ? '' : value)}
              options={PUBLISHING_STATUS_OPTIONS}
              className="w-full"
            />
          </div>

          {/* Platform Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {t('platform')}
            </label>
            <PlatformMultiSelect
              size="sm"
              value={filters.platforms || []}
              onChange={(values) => setFilter('platforms', values.length > 0 ? values : null)}
              allOptionLabel={t('all_platforms')}
              placeholder={t('all_platforms')}
              className="w-full"
            />
          </div>

          {/* Labels Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {t('labels')}
            </label>
            <TagsMultiSelect
              size="sm"
              value={selectedTags}
              onChange={handleTagsChange}
              availableTags={availableLabels.map(label => ({
                name: label.name,
                count: label.count,
              }))}
              placeholder={t('filter_by_labels')}
              className="w-full"
            />
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {t('date_range')}
            </label>
            <DateRangePicker
              size="sm"
              value={dateRange}
              onChange={handleDateRangeChange}
              placeholder={t('select_date_range')}
              className="w-full"
            />
          </div>

          {/* Advanced Filters Section */}
          <div className="pt-4 sm:pt-6 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 sm:mb-4">{t('advanced_filters')}</h3>

            {/* Sort By */}
            <div className="mb-3 sm:mb-4">
              <Select
                size="sm"
                label={t('sort_by')}
                value={filters.sortBy || 'newest'}
                onValueChange={(value) => setFilter('sortBy', value as 'newest' | 'oldest' | 'scheduled_asc' | 'scheduled_desc')}
                options={SORT_OPTIONS}
                className="w-full"
              />
            </div>

            {/* Media Type Filter */}
            <div className="mb-3 sm:mb-4">
              <Select
                size="sm"
                label={t('media_type')}
                value={filters.mediaType || 'all'}
                onValueChange={(value) => setFilter('mediaType', value === 'all' ? null : value)}
                options={MEDIA_TYPE_OPTIONS}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 sm:px-6 py-4 pb-6 sm:pb-4 flex gap-1.5 sm:gap-3">
          <Button
            variant="primary"
            onClick={onClose}
            className="flex-1 w-full"
          >
            {t('apply_filters')}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="secondary"
              onClick={() => {
                clearFilters();
                onClose();
              }}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <Icon name={ICONS.CROSS} size="sm" />
              {t('clear_all')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
